import { FC, Fragment, ReactNode } from "react";
import { Platform, type ViewProps } from "react-native";
import * as AlertDialogPrimitive from "@rn-primitives/alert-dialog";
import { FadeIn, FadeOut } from "react-native-reanimated";
import { FullWindowOverlay as RNFullWindowOverlay } from "react-native-screens";

import { TextClassContext } from "@/context/TextClassContext";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { ChildrenOrI18nType } from "@/i18n";
import { cn } from "@/utils/tailwind";

import { VnBox, VnThemedBox } from "./VnBox";
import { VnButton, VnButtonPropsType } from "./VnButton/VnButton";
import { VnNativeOnlyAnimatedView } from "./VnNativeOnlyAnimatedView";
import { VnText } from "./VnText/VnText";

export const VnAlertDialog = AlertDialogPrimitive.Root;

export const VnAlertDialogTrigger = AlertDialogPrimitive.Trigger;

export const VnAlertDialogPortal = AlertDialogPrimitive.Portal;

const FullWindowOverlay =
  Platform.OS === "ios" ? RNFullWindowOverlay : Fragment;

type VnAlertDialogOverlayPropsType = Omit<
  AlertDialogPrimitive.OverlayProps,
  "asChild"
> & {
  children?: ReactNode;
};

export const VnAlertDialogOverlay: FC<VnAlertDialogOverlayPropsType> = ({
  className,
  children,
  ...props
}) => {
  const { isWeb, isNative } = usePlatformOS();

  return (
    <FullWindowOverlay>
      <AlertDialogPrimitive.Overlay
        className={cn(
          "absolute bottom-0 left-0 right-0 top-0 z-50 flex justify-center bg-black/50 p-2",
          isWeb && "animate-in fade-in-0 fixed",
          isNative && "px-6",
          className,
        )}
        {...props}>
        <VnNativeOnlyAnimatedView
          entering={FadeIn.duration(200).delay(50)}
          exiting={FadeOut.duration(150)}>
          <>{children}</>
        </VnNativeOnlyAnimatedView>
      </AlertDialogPrimitive.Overlay>
    </FullWindowOverlay>
  );
};

type VnAlertDialogContentPropsType = AlertDialogPrimitive.ContentProps & {
  portalHost?: string;
};

export const VnAlertDialogContent: FC<VnAlertDialogContentPropsType> = ({
  className,
  portalHost,
  ...props
}) => {
  const { isWeb } = usePlatformOS();

  return (
    <VnAlertDialogPortal hostName={portalHost}>
      <VnAlertDialogOverlay>
        <VnThemedBox>
          <AlertDialogPrimitive.Content
            className={cn(
              "border-border bg-card z-50 flex w-full max-w-[calc(100%-2rem)] flex-col gap-4 self-center rounded-lg border p-6 shadow-lg shadow-black/5 sm:max-w-lg",
              isWeb && "animate-in fade-in-0 zoom-in-95 duration-200",
              className,
            )}
            {...props}
          />
        </VnThemedBox>
      </VnAlertDialogOverlay>
    </VnAlertDialogPortal>
  );
};

export const VnAlertDialogHeader: FC<ViewProps> = ({ className, ...props }) => {
  return (
    <TextClassContext.Provider value="text-center sm:text-left">
      <VnBox className={cn("flex flex-col gap-2", className)} {...props} />
    </TextClassContext.Provider>
  );
};

export const VnAlertDialogFooter: FC<ViewProps> = ({ className, ...props }) => {
  return (
    <VnBox
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
};

type VnAlertDialogTitlePropsType = AlertDialogPrimitive.TitleProps &
  ChildrenOrI18nType;

export const VnAlertDialogTitle: FC<VnAlertDialogTitlePropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  return (
    <AlertDialogPrimitive.Title {...props}>
      {children ?? (
        <VnText
          variant="headerTwo"
          i18nKey={i18nKey}
          i18nOptions={i18nOptions}
          className={cn("leading-none", className)}
        />
      )}
    </AlertDialogPrimitive.Title>
  );
};

type VnAlertDialogDescriptionPropsType = AlertDialogPrimitive.DescriptionProps &
  ChildrenOrI18nType;

export const VnAlertDialogDescription: FC<
  VnAlertDialogDescriptionPropsType
> = ({ className, children, i18nKey, i18nOptions, ...props }) => {
  return (
    <AlertDialogPrimitive.Description {...props}>
      {children ?? (
        <VnText
          i18nKey={i18nKey}
          i18nOptions={i18nOptions}
          className={cn("text-muted-foreground", className)}
        />
      )}
    </AlertDialogPrimitive.Description>
  );
};

type VnAlertDialogActionPropsType = Omit<
  AlertDialogPrimitive.ActionProps,
  "children"
> &
  VnButtonPropsType;

export const VnAlertDialogAction: FC<VnAlertDialogActionPropsType> = props => {
  return (
    <AlertDialogPrimitive.Action asChild>
      <VnButton {...props} />
    </AlertDialogPrimitive.Action>
  );
};

type VnAlertDialogCancelPropsType = Omit<
  AlertDialogPrimitive.CancelProps,
  "children"
> &
  VnButtonPropsType;

export const VnAlertDialogCancel: FC<VnAlertDialogCancelPropsType> = ({
  className,
  ...props
}) => {
  return (
    <AlertDialogPrimitive.Cancel asChild>
      <VnButton variant="outline" className={className} {...props} />
    </AlertDialogPrimitive.Cancel>
  );
};
